import React, { createContext, useContext, useEffect } from 'react';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '../lib/firebase/config';
import { useAuth } from './AuthContext';
import { toast } from 'react-hot-toast';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  isLoading: boolean;
}

// Initialize with default values
const defaultContext: ThemeContextType = {
  theme: 'dark',
  setTheme: () => {},
  isLoading: true
};

const ThemeContext = createContext<ThemeContextType>(defaultContext);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = React.useState<Theme>('dark');
  const [isLoading, setIsLoading] = React.useState(true);
  const { currentUser } = useAuth();

  // Load theme from localStorage first
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') as Theme;
    if (savedTheme) {
      setThemeState(savedTheme);
    }
    setIsLoading(false);
  }, []);

  // Sync with Firebase if user is logged in
  useEffect(() => {
    if (!currentUser?.uid) {
      setIsLoading(false);
      return;
    }

    const themeRef = doc(db, 'themes', currentUser.uid);
    setIsLoading(true);
    
    const unsubscribe = onSnapshot(themeRef, 
      (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          setThemeState(data.theme);
          localStorage.setItem('theme', data.theme);
        }
        setIsLoading(false);
      },
      (error) => {
        console.error('Error loading theme:', error);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentUser?.uid]);

  const setTheme = async (newTheme: Theme) => {
    setThemeState(newTheme);
    localStorage.setItem('theme', newTheme);

    if (currentUser?.uid) {
      try {
        const themeRef = doc(db, 'themes', currentUser.uid);
        await setDoc(themeRef, { theme: newTheme }, { merge: true });
      } catch (error) {
        console.error('Error saving theme:', error);
        toast.error('Failed to save theme preference');
      }
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, isLoading }}>
      <div className={`app ${theme}`}>
        {children}
      </div>
    </ThemeContext.Provider>
  );
};